import React, { useEffect, useState } from 'react';
import {
  LuSparkles,
  LuBot,
  LuPlay,
  LuChartColumn,
  LuCircleCheck,
  LuShieldCheck,
  LuGitBranch,
  LuZap,
} from 'react-icons/lu';

const STAGES = [
  { id: 'create', label: 'Test Creation', meta: 'AI drafts cases from stories', icon: LuSparkles },
  { id: 'automate', label: 'Automation', meta: 'Scripts generated & versioned', icon: LuBot },
  { id: 'execute', label: 'Execution', meta: 'Parallel runs across browsers', icon: LuPlay },
  { id: 'results', label: 'Results', meta: 'Insights, trends & flaky tests', icon: LuChartColumn },
];

const STAGE_MS = 2800;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const CreatePanel = () => (
  <div className="asv-panel__body asv-create">
    {['Open checkout as guest user', 'Add two items to the cart', 'Apply promo code SAVE10', 'Verify order total updates'].map(
      (step, i) => (
        <div className="asv-line" style={{ animationDelay: `${i * 260}ms` }} key={step}>
          <span className="asv-line__num">{i + 1}</span>
          <span className="asv-line__text">{step}</span>
        </div>
      )
    )}
  </div>
);

const AutomatePanel = () => (
  <div className="asv-panel__body asv-code">
    {[
      ["test('guest checkout', async ({ page }) => {", 0],
      ["await page.goto('/checkout');", 1],
      ["await cart.add(['sku-104', 'sku-221']);", 1],
      ["await promo.apply('SAVE10');", 1],
      ['await expect(total).toHaveText(/\\$/);', 1],
      ['});', 0],
    ].map(([code, indent], i) => (
      <div className="asv-code__line" style={{ animationDelay: `${i * 200}ms`, paddingLeft: indent * 16 }} key={code}>
        {code}
      </div>
    ))}
  </div>
);

const ExecutePanel = () => (
  <div className="asv-panel__body asv-exec">
    {[
      ['Chrome · Desktop', 100],
      ['Firefox · Desktop', 86],
      ['Safari · Mobile', 64],
    ].map(([name, pct], i) => (
      <div className="asv-exec__row" key={name}>
        <span className="asv-exec__name">{name}</span>
        <span className="asv-exec__track">
          <span className="asv-exec__fill" style={{ '--asv-w': `${pct}%`, animationDelay: `${i * 180}ms` }} />
        </span>
      </div>
    ))}
  </div>
);

const ResultsPanel = () => (
  <div className="asv-panel__body asv-results">
    <div className="asv-ring">
      <svg viewBox="0 0 36 36">
        <circle cx="18" cy="18" r="15.5" className="asv-ring__track" />
        <circle cx="18" cy="18" r="15.5" className="asv-ring__value" />
      </svg>
      <span className="asv-ring__label">
        <LuCircleCheck size={14} />
        Passing
      </span>
    </div>
    <div className="asv-bars">
      {[42, 58, 50, 72, 66, 84, 92].map((h, i) => (
        <span key={i} style={{ '--asv-h': `${h}%`, animationDelay: `${i * 70}ms` }} />
      ))}
    </div>
  </div>
);

const PANELS = [CreatePanel, AutomatePanel, ExecutePanel, ResultsPanel];

/**
 * Decorative product visualisation for the sign-in page (no live data).
 */
const AuthShowcase = () => {
  const [active, setActive] = useState(() => (prefersReducedMotion() ? STAGES.length - 1 : 0));

  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const timer = setInterval(() => setActive((i) => (i + 1) % STAGES.length), STAGE_MS);
    return () => clearInterval(timer);
  }, []);

  const Panel = PANELS[active];

  return (
    <div className="auth-showcase">
      <div className="auth-showcase__brand">
        <img src="/robot-logo.png" alt="" />
        <span>RobiTest</span>
      </div>

      <div className="auth-showcase__copy">
        <h2>Ship reliable tests, faster</h2>
        <p>From user story to green build — create, automate, run and analyse tests in one workspace.</p>
      </div>

      <div className="asv-window" aria-hidden="true">
        <div className="asv-window__bar">
          <span className="asv-window__dots">
            <i />
            <i />
            <i />
          </span>
          <span className="asv-window__title">checkout-flow · regression suite</span>
        </div>

        <ol className="asv-pipeline">
          {STAGES.map((stage, i) => {
            const Icon = stage.icon;
            const state = i === active ? 'is-active' : i < active ? 'is-done' : '';
            return (
              <li className={`asv-stage ${state}`} key={stage.id}>
                <span className="asv-stage__icon">
                  <Icon size={15} />
                </span>
                <span className="asv-stage__text">
                  <span className="asv-stage__label">{stage.label}</span>
                  <span className="asv-stage__meta">{stage.meta}</span>
                </span>
              </li>
            );
          })}
        </ol>

        <div className="asv-panel" key={active}>
          <div className="asv-panel__head">
            <span className="asv-panel__dot" />
            {STAGES[active].label}
          </div>
          <Panel />
        </div>
      </div>

      <ul className="auth-showcase__points">
        <li>
          <LuShieldCheck size={15} /> Workspace roles &amp; permissions
        </li>
        <li>
          <LuGitBranch size={15} /> Jira &amp; Bitbucket integrations
        </li>
        <li>
          <LuZap size={15} /> AI-assisted test generation
        </li>
      </ul>
    </div>
  );
};

export default AuthShowcase;
